import React, { useEffect, useMemo, useState } from 'react';
import './NotificationPanel.css';
import { normaliseraNotisLank } from '../utils/notisLank';
import { tolkaTidpunkt, TIDSZON } from '../utils/tidsstampel';

/* ───────────────────────────────────────────────
   Helper: classify notification type → icon colour
   ─────────────────────────────────────────────── */
function typeToStyle(type) {
  switch (type) {
    case 'issue':
    case 'issue_status':
    case 'task':
    case 'purchasing':
      return 'blue';
    case 'document':
    case 'doc':
      return 'green';
    case 'revision':
    case 'warning':
    case 'dagbok':
      return 'amber';
    case 'chat':
    case 'message':
    case 'inbox':
      return 'purple';
    case 'error':
      return 'red';
    default:
      return 'blue'; // system / status changes
  }
}

/* SVG icon per notification type */
function TypeIcon({ type }) {
  const t = type || 'system';
  if (['issue', 'issue_status', 'task', 'system', 'purchasing'].includes(t)) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
    );
  }
  if (['document', 'doc'].includes(t)) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    );
  }
  if (['revision', 'warning', 'dagbok'].includes(t)) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  if (['chat', 'message', 'inbox'].includes(t)) {
    return (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );
  }
  // default — circle-info
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

/* Map type to filter tab category */
function typeToCategory(type) {
  if (['issue', 'issue_status', 'task', 'purchasing'].includes(type)) return 'issues';
  if (['document', 'doc'].includes(type)) return 'documents';
  if (['chat', 'message', 'inbox'].includes(type)) return 'messages';
  return 'issues'; // system / revision / etc. default to issues
}

/* Format source chip text */
function sourceChip(n) {
  // Try to extract something meaningful for the chip
  if (n.type === 'purchasing') return 'Inköp';
  if (n.type === 'revision') return 'Revision';
  if (n.type === 'chat' || n.type === 'message' || n.type === 'inbox') return 'Meddelande';
  if (n.type === 'document' || n.type === 'doc') return 'Dokument';
  if (n.type === 'dagbok') return 'Dagbok';
  // Try to extract issue/ärende reference from message
  const m = n.message || '';
  const iss = m.match(/ärende[:\s]+(.{1,30})/i);
  if (iss) return 'Ärende';
  return n.type || 'System';
}

/* Compact time formatting */
function formatTime(dateStr) {
  try {
    const d = tolkaTidpunkt(dateStr);
    if (!d) return '';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', timeZone: TIDSZON });
    if (sameDay) return time;
    if (isYesterday) return `igår ${time}`;
    return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', timeZone: TIDSZON }) + ' ' + time;
  } catch {
    return '';
  }
}

/* Group label: Idag / Igår / date */
function dateGroup(dateStr) {
  try {
    const d = tolkaTidpunkt(dateStr);
    if (!d) return 'Övrigt';
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Idag';
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Igår';
    return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', timeZone: TIDSZON });
  } catch {
    return 'Övrigt';
  }
}

/* Source icon (small) */
function SourceIcon({ type }) {
  if (['issue', 'issue_status', 'task', 'system', 'purchasing'].includes(type)) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    );
  }
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

const TABS = [
  { key: 'all', label: 'Alla' },
  { key: 'issues', label: 'Ärenden', icon: 'issue' },
  { key: 'documents', label: 'Dokument', icon: 'doc' },
  { key: 'messages', label: 'Meddelanden', icon: 'msg' },
];

function TabIcon({ type }) {
  if (type === 'issue') return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    </svg>
  );
  if (type === 'doc') return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><polyline points="13 2 13 9 20 9" />
    </svg>
  );
  if (type === 'msg') return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
  return null;
}


/* ═══════════════════════════════════════════
   NotificationPanel component
   ═══════════════════════════════════════════ */
export default function NotificationPanel({
  items = [],
  loading = false,
  unreadCount = 0,
  marking = false,
  onMarkAllRead,
  onMarkOneRead,
  onOpenNotification,
  onClose,
  onViewAll,
}) {
  const [activeTab, setActiveTab] = useState('all');

  // Esc stänger notislistan (och dess bakgrund i Layout via onClose). Fångstfas +
  // preventDefault så att en högerpanel (KpPanel) under inte också stängs (DS Del Y5, HP-00f).
  useEffect(() => {
    if (typeof onClose !== 'function') return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  // Gamla ärendenotiser pekar på /issues/{id}, som saknar route.
  const oppnaNotis = (n) => onOpenNotification?.({ ...n, link: normaliseraNotisLank(n.link) });

  /* Filter items by active tab */
  const filtered = useMemo(() => {
    if (activeTab === 'all') return items;
    return items.filter(n => typeToCategory(n.type) === activeTab);
  }, [items, activeTab]);

  /* Group by date */
  const grouped = useMemo(() => {
    const groups = [];
    let lastGroup = '';
    for (const n of filtered) {
      const g = dateGroup(n.created_at);
      if (g !== lastGroup) {
        groups.push({ label: g, items: [] });
        lastGroup = g;
      }
      groups[groups.length - 1].items.push(n);
    }
    return groups;
  }, [filtered]);

  /* Tab counts */
  const tabCounts = useMemo(() => {
    const c = { all: 0, issues: 0, documents: 0, messages: 0 };
    for (const n of items) {
      if (!n.is_read) {
        c.all++;
        const cat = typeToCategory(n.type);
        if (c[cat] !== undefined) c[cat]++;
      }
    }
    return c;
  }, [items]);

  return (
    <div role="menu" aria-label="Notifieringar" className="np-panel" onClick={e => e.stopPropagation()}>
      {/* ─── Header ─── */}
      <div className="np-header">
        <div className="np-title">
          Notifieringar
          {unreadCount > 0 && <span className="np-count">{unreadCount} nya</span>}
        </div>
        <div className="np-actions">
          <button className="np-btn" onClick={onMarkAllRead} disabled={marking}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
            Markera alla lästa
          </button>
        </div>
      </div>

      {/* ─── Filter tabs ─── */}
      <div className="np-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`np-tab${activeTab === tab.key ? ' np-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon && <TabIcon type={tab.icon} />}
            {tab.label}
            {tabCounts[tab.key] > 0 && (
              <span className="np-tab-count">{tabCounts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* ─── Items list ─── */}
      <div className="np-list">
        {loading ? (
          <div className="np-empty">Laddar…</div>
        ) : filtered.length === 0 ? (
          <div className="np-empty">Inga notifieringar</div>
        ) : (
          grouped.map((group, gi) => (
            <React.Fragment key={gi}>
              <div className="np-group">{group.label}</div>
              {group.items.map(n => {
                const style = typeToStyle(n.type);
                return (
                  <div
                    key={n.id}
                    className={`np-item${!n.is_read ? ' np-item--unread' : ''}`}
                    onClick={() => oppnaNotis(n)}
                  >
                    <div className={`np-icon np-icon--${style}`}>
                      <TypeIcon type={n.type} />
                    </div>
                    <div className="np-content">
                      <div className="np-text">
                        {n.link ? (
                          <span className="np-link">{n.message || n.type}</span>
                        ) : (
                          <span>{n.message || n.type}</span>
                        )}
                      </div>
                      <div className="np-meta">
                        <span className="np-time">{formatTime(n.created_at)}</span>
                        <span className="np-source">
                          <SourceIcon type={n.type} />
                          {sourceChip(n)}
                        </span>
                      </div>
                    </div>
                    <div className="np-item-actions">
                      {!n.is_read && (
                        <button
                          className="np-ia np-ia--read"
                          title="Markera läst"
                          onClick={e => { e.stopPropagation(); onMarkOneRead?.(n.id); }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </button>
                      )}
                      <button
                        className="np-ia"
                        title="Öppna"
                        onClick={e => { e.stopPropagation(); oppnaNotis(n); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}
      </div>

      {/* ─── Footer ─── */}
      <div className="np-footer">
        <button className="np-footer-link" onClick={onViewAll}>
          Visa alla notifieringar
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
    </div>
  );
}
